import "animate.css/animate.min.css";
import "bootswatch/dist/darkly/bootstrap.min.css";
import React, { Suspense } from "react";
import ReactDOM from "react-dom/client";
import { createBrowserRouter, redirect, RouterProvider } from "react-router-dom";
import "./index.scss";

import { LoadingSpinner } from "./components/LoadingSpinner";
import { ErrorPage } from "./ErrorPage";
import { startAnalytics } from "./firebase/analytics";
import { LocationProvider } from "./providers/LocationProvider";

import * as LazyRoutes from "./routes/LazyRoutes";
import { reloadForNewBuild } from "./utils/staleBuild";

// Fired when a lazy route's chunk is missing, which happens to tabs still
// running the previous build after a deploy. Reload to pick up the new one;
// if that's already been tried, the error carries on to the ErrorPage.
window.addEventListener("vite:preloadError", function () {
  reloadForNewBuild();
});

startAnalytics();

const router = createBrowserRouter([
  {
    path: "/preview/awards",
    lazy: async () => await import("./routes/AwardsPreview"),
  },
  {
    path: "/",
    element: (
      <Suspense fallback={<LoadingSpinner />}>
        <LazyRoutes.Root />
      </Suspense>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        path: "",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <LazyRoutes.LeaderboardPage />
          </Suspense>
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: "/profile/:id",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <LazyRoutes.ProfilePage />
          </Suspense>
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: "/sessions/:id",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <LazyRoutes.SessionPage />
          </Suspense>
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: "/venues",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <LazyRoutes.VenuesPage />
          </Suspense>
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: "/venues/:id",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <LazyRoutes.VenuePage />
          </Suspense>
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: "/thumb-or-cum/:id?",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <LazyRoutes.ThumbOrCumPage />
          </Suspense>
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: "/lotto/:drawKey",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <LazyRoutes.LottoDrawPage />
          </Suspense>
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: "/shop",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <LazyRoutes.ShopRoutes />
          </Suspense>
        ),
        errorElement: <ErrorPage />,
        children: [
          {
            path: "/shop",
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <LazyRoutes.ShopPage />
              </Suspense>
            ),
            errorElement: <ErrorPage />,
          },
          {
            path: "/shop/inventory",
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <LazyRoutes.InventoryPage />
              </Suspense>
            ),
            errorElement: <ErrorPage />,
          },
        ],
      },
      {
        path: "/chat",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <LazyRoutes.ChatRoutes />
          </Suspense>
        ),
        errorElement: <ErrorPage />,
        children: [
          {
            path: "/chat",
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <LazyRoutes.ChatEmptyState />
              </Suspense>
            ),
            errorElement: <ErrorPage />,
          },
          {
            path: "/chat/:groupId",
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <LazyRoutes.ChatGroupPage />
              </Suspense>
            ),
            errorElement: <ErrorPage />,
          },
        ],
      },
      {
        path: "/championships",
        HydrateFallback: LoadingSpinner,
        lazy: async () => await import("./routes/championships"),
      },
      {
        path: "/championships/:id",
        HydrateFallback: LoadingSpinner,
        lazy: async () => await import("./routes/championships/[id]"),
      },
      {
        path: "/crokinole",
        loader: () => redirect("/cock-in-hole"),
      },
      {
        path: "/cock-in-hole",
        HydrateFallback: LoadingSpinner,
        lazy: async () => await import("./routes/cock-in-hole"),
      },
      {
        path: "/awards",
        HydrateFallback: LoadingSpinner,
        lazy: async () => await import("./routes/AwardsPage"),
      },
      {
        path: "/ledger",
        HydrateFallback: LoadingSpinner,
        lazy: async () => await import("./routes/ledger"),
      },
      {
        path: "/sweepstakes",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <LazyRoutes.SweepstakesPage />
          </Suspense>
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: "/sweepstakes/:id",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <LazyRoutes.SweepstakePage />
          </Suspense>
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: "/leaderboards",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <LazyRoutes.LeaderboardRoutes />
          </Suspense>
        ),
        errorElement: <ErrorPage />,
        children: [
          {
            path: "/leaderboards",
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <LazyRoutes.LeaderboardMonthly />
              </Suspense>
            ),
            errorElement: <ErrorPage />,
          },
          {
            path: "/leaderboards/weekly",
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <LazyRoutes.LeaderboardWeekly />
              </Suspense>
            ),
            errorElement: <ErrorPage />,
          },
          {
            path: "/leaderboards/yearly",
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <LazyRoutes.LeaderboardYearly />
              </Suspense>
            ),
            errorElement: <ErrorPage />,
          },
        ],
      },
      {
        path: "/manage",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <LazyRoutes.AdminRoutes />
          </Suspense>
        ),
        errorElement: <ErrorPage />,
        children: [
          {
            path: "/manage",
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <LazyRoutes.ManageProfilesPage />
              </Suspense>
            ),
            errorElement: <ErrorPage />,
          },
          {
            path: "/manage/profiles",
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <LazyRoutes.ManageProfilesPage />
              </Suspense>
            ),
            errorElement: <ErrorPage />,
          },
          {
            path: "/manage/venues",
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <LazyRoutes.ManageVenuesPage />
              </Suspense>
            ),
            errorElement: <ErrorPage />,
          },
          {
            path: "/manage/tools",
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <LazyRoutes.ManageToolsPage />
              </Suspense>
            ),
            errorElement: <ErrorPage />,
          },
          {
            path: "/manage/brobot",
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <LazyRoutes.ManageBroBotPage />
              </Suspense>
            ),
            errorElement: <ErrorPage />,
          },
        ],
      },
      {
        path: "/morsals",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <LazyRoutes.MorsalRoutes />
          </Suspense>
        ),
        errorElement: <ErrorPage />,
        children: [
          {
            path: "/morsals",
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <LazyRoutes.MorsalsPage show="current" />
              </Suspense>
            ),
            errorElement: <ErrorPage />,
          },
          {
            path: "/morsals/new",
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <LazyRoutes.NewMorsalPage />
              </Suspense>
            ),
            errorElement: <ErrorPage />,
          },
          {
            path: "/morsals/old",
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <LazyRoutes.MorsalsPage show="old" />
              </Suspense>
            ),
            errorElement: <ErrorPage />,
          },
        ],
      },
      {
        path: "/quotes",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <LazyRoutes.QuoteRouletteRoutes />
          </Suspense>
        ),
        children: [
          {
            path: "/quotes",
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <LazyRoutes.QuoteRoulettePage />
              </Suspense>
            ),
            errorElement: <ErrorPage />,
          },
          {
            path: "/quotes/leaderboard",
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <LazyRoutes.QuoteRouletteLeaderboard />
              </Suspense>
            ),
            errorElement: <ErrorPage />,
          },
        ],
        errorElement: <ErrorPage />,
      },
    ],
  },
]);

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <LocationProvider options={{}}>
      <RouterProvider router={router} />
    </LocationProvider>
  </React.StrictMode>,
);

if (navigator.serviceWorker) {
  navigator.serviceWorker?.register("/firebase-messaging-sw.js").catch(console.error);
}
